import { ButtonHTMLAttributes, forwardRef, ReactNode } from 'react'
import styled from 'styled-components'

export interface KbdChipButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Optional small counter badge in the top-end corner. Only rendered when > 0. */
  count?: number
  /** Visual emphasis variant. */
  variant?: 'default' | 'accent'
  children?: ReactNode
}

const KbdChipButton = forwardRef<HTMLButtonElement, KbdChipButtonProps>(
  ({ count, variant = 'default', type = 'button', children, ...rest }, ref) => (
    <Btn ref={ref} type={type} $variant={variant} {...rest}>
      {children}
      {typeof count === 'number' && count > 0 && <Counter>{count}</Counter>}
    </Btn>
  ),
)

KbdChipButton.displayName = 'KbdChipButton'

export default KbdChipButton

const Btn = styled.button<{ $variant: 'default' | 'accent' }>`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
  height: 2.25rem;
  min-width: 2.25rem;
  padding: 0 0.5rem;
  border-radius: 0.5rem;
  background: ${({ theme, $variant }) =>
    $variant === 'accent' ? theme.colors.accent : theme.colors.line2};
  color: ${({ theme, $variant }) =>
    $variant === 'accent' ? theme.colors.ink : theme.colors.icon};
  border: 1px solid
    ${({ theme, $variant }) =>
      $variant === 'accent' ? theme.colors.accent : theme.colors.line};
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: ${({ theme }) => theme.text.xs};
  font-weight: 600;
  cursor: pointer;
  user-select: none;
  transition: all ${({ theme }) => theme.motion.fast} ease;

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.accent};
    color: ${({ theme }) => theme.colors.ink};
    border-color: ${({ theme }) => theme.colors.accent};
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }

  svg {
    width: 1rem;
    height: 1rem;
  }
`

const Counter = styled.span`
  position: absolute;
  min-width: 1.125rem;
  height: 1.125rem;
  top: -0.375rem;
  inset-inline-end: -0.375rem;
  padding: 0 0.25rem;
  background: ${({ theme }) => theme.colors.accent};
  border-radius: 999px;
  color: ${({ theme }) => theme.colors.ink};
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: ${({ theme }) => theme.text.xs};
  font-weight: 600;
  line-height: 1.125rem;
  text-align: center;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
`
